import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import PostCommentModal from '../components/PostCommentModal';

function SocialFeed() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [savedPosts, setSavedPosts] = useState(new Set());
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentModalPost, setCommentModalPost] = useState(null);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchPosts();
      // Polling removed as per user request
    }
  }, [userId]);

  const checkSession = async () => {
    try {
      const res = await api.get('user/session-check/');
      if (res.data.authenticated) {
        setUserId(res.data.user_id);
      } else {
        navigate('/user/login');
      }
    } catch (err) {
      console.error("Session check failed:", err);
      navigate('/user/login');
    }
  };

  const fetchPosts = async () => {
    try {
      const res = await api.get('social/posts/', {
        params: { user_id: userId }
      });
      const sortedPosts = res.data.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );
      setPosts(sortedPosts);

      const liked = new Set();
      const saved = new Set();
      sortedPosts.forEach(post => {
        if (post.is_liked) liked.add(post.id);
        if (post.is_saved) saved.add(post.id);
      });
      setLikedPosts(liked);
      setSavedPosts(saved);
      return sortedPosts;
    } catch (err) {
      console.error("Error fetching posts:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const syncCommentModalPost = (sortedPosts) => {
    if (!commentModalPost || !sortedPosts) return;
    const updated = sortedPosts.find((p) => p.id === commentModalPost.id);
    if (updated) setCommentModalPost(updated);
  };

  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) {
      console.log('No image provided to getImageUrl');
      return null;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    // If already a full URL, return as is
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    // If starts with /media/, append to API URL
    if (image.startsWith('/media/')) {
      const fullUrl = `${apiUrl}${image}`;
      console.log('Constructed image URL:', fullUrl);
      return fullUrl;
    }

    // Otherwise, assume it's a relative path and add /media/
    const fullUrl = `${apiUrl}/media/${image}`;
    console.log('Constructed image URL (with /media/):', fullUrl);
    return fullUrl;
  };

  // Handle like/unlike
  const handleLike = async (postId) => {
    const isLiked = likedPosts.has(postId);

    // Optimistic update
    if (isLiked) {
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, likes_count: Math.max(0, p.likes_count - 1) } : p
      ));
    } else {
      setLikedPosts(prev => new Set(prev).add(postId));
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, likes_count: (p.likes_count || 0) + 1 } : p
      ));
    }

    try {
      await api.post(`social/posts/${postId}/like/`, {
        user_id: userId
      });
      const sorted = await fetchPosts();
      syncCommentModalPost(sorted);
    } catch (err) {
      console.error("Error liking post:", err);
      // Revert optimistic update on error
      if (isLiked) {
        setLikedPosts(prev => new Set(prev).add(postId));
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, likes_count: (p.likes_count || 0) + 1 } : p
        ));
      } else {
        setLikedPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, likes_count: Math.max(0, p.likes_count - 1) } : p
        ));
      }
    }
  };

  const handleSave = async (postId) => {
    const isSaved = savedPosts.has(postId);

    if (isSaved) {
      setSavedPosts(prev => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
    } else {
      setSavedPosts(prev => new Set(prev).add(postId));
    }

    try {
      await api.post(`social/posts/${postId}/save/`, { user_id: userId });
      const sorted = await fetchPosts();
      syncCommentModalPost(sorted);
      toast.success(isSaved ? 'Post removed from saved' : 'Post saved!', {
        position: 'bottom-right',
        autoClose: 2000,
      });
    } catch (err) {
      console.error('Error saving post:', err);
      if (isSaved) {
        setSavedPosts(prev => new Set(prev).add(postId));
      } else {
        setSavedPosts(prev => {
          const next = new Set(prev);
          next.delete(postId);
          return next;
        });
      }
      toast.error('Failed to save post. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    }
  };

  const openCommentModal = (post) => {
    setCommentText('');
    setCommentModalPost(post);
  };

  const closeCommentModal = () => {
    setCommentModalPost(null);
    setCommentText('');
  };

  const handleComment = async () => {
    if (!commentModalPost) return;
    const comment = commentText?.trim();
    if (!comment) return;

    try {
      await api.post(`social/posts/${commentModalPost.id}/add_comment/`, {
        user_id: userId,
        content: comment
      });
      setCommentText('');
      const sorted = await fetchPosts();
      syncCommentModalPost(sorted);
    } catch (err) {
      console.error("Error adding comment:", err);
      toast.error("Failed to add comment. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  // Generate share link
  const handleShare = async (postId) => {
    try {
      const res = await api.get(`social/posts/${postId}/generate_share_link/`);
      if (res.data.share_link) {
        // Copy to clipboard
        navigator.clipboard.writeText(res.data.share_link);
        toast.success("Share link copied to clipboard!", {
          position: "bottom-right",
          autoClose: 3000,
        });
        // Refresh to update post with share link
        fetchPosts();
      }
    } catch (err) {
      console.error("Error generating share link:", err);
      toast.error("Failed to generate share link. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <UserNavbar />
      <div className="container py-4">

        {/* HEADER SECTION */}
        <div className="row mb-4 align-items-center">
          <div className="col-12 col-md-8">
            <h2 className="fw-bold text-dark mb-0 fs-4 fs-md-3">
              <i className="bi bi-heart-pulse me-2 text-primary"></i>Health Feed
            </h2>
            <p className="text-muted mb-0 mb-md-2">Discover health tips and insights from our expert doctors.</p>
          </div>
          <div className="col-12 col-md-4 mt-3 mt-md-0">
            <div className="d-flex gap-2 justify-content-md-end">
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${!showSavedOnly ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setShowSavedOnly(false)}
              >
                All Posts
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${showSavedOnly ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setShowSavedOnly(true)}
              >
                <i className="bi bi-bookmark-fill me-1"></i>Saved
              </button>
            </div>
          </div>
        </div>

        {/* POSTS FEED */}
        {(() => {
          const visiblePosts = showSavedOnly
            ? posts.filter((p) => savedPosts.has(p.id))
            : posts;
          return visiblePosts.length === 0 ? (
          <div className="text-center py-5">
            <div className="card border-0 shadow-sm">
              <div className="card-body py-5">
                <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                <h5 className="fw-bold">{showSavedOnly ? 'No saved posts' : 'No posts available'}</h5>
                <p className="text-muted">
                  {showSavedOnly
                    ? 'Tap the bookmark icon on a post to save it here.'
                    : 'Check back later for health tips from our doctors.'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {visiblePosts.map((post) => {
              const isLiked = likedPosts.has(post.id);
              const isSaved = savedPosts.has(post.id);
              // Use short_content for card display, or truncate large_content
              const shortDesc = post.short_content || '';
              const fullContent = post.large_content || post.short_content || '';
              // Strip HTML tags for length check
              const textContent = shortDesc.replace(/<[^>]*>/g, '');
              const displayContent = textContent.length > 150
                ? textContent.substring(0, 150) + '...'
                : textContent;

              return (
                <div key={post.id} className="col-12 col-md-6 col-lg-4">
                  <div className="card border-0 shadow-sm h-100 hover-shadow" style={{ cursor: 'pointer' }} onClick={() => navigate(`/post/${post.id}`)}>
                    {/* POST IMAGE */}
                    {post.image && (
                      <div className="position-relative" style={{ height: '200px', overflow: 'hidden' }}>
                        <img
                          src={getImageUrl(post.image)}
                          alt={post.title || post.doctor_name || 'Post image'}
                          className="w-100 h-100"
                          style={{
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Available';
                            e.target.style.opacity = '0.5';
                          }}
                          loading="lazy"
                        />
                      </div>
                    )}

                    <div className="card-body d-flex flex-column">
                      {/* POST HEADER */}
                      <div className="d-flex align-items-center mb-2">
                        <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-2 me-2">
                          <i className="bi bi-person-badge text-white"></i>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="fw-bold mb-0 small">{post.doctor_name || 'Doctor'}</h6>
                          <small className="text-muted">
                            {new Date(post.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </small>
                        </div>
                      </div>

                      {/* POST TITLE */}
                      {post.title && (
                        <h6 className="fw-bold mb-2">{post.title}</h6>
                      )}

                      {/* POST CONTENT - SHORT DESCRIPTION */}
                      <div
                        className="mb-3 flex-grow-1"
                        style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(displayContent)
                        }}
                      />

                      {/* POST ACTIONS */}
                      <div className="d-flex align-items-center justify-content-between border-top pt-3 mt-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex gap-3">
                          <button
                            className="btn btn-link p-0 text-decoration-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLike(post.id);
                            }}
                            style={{ border: 'none', background: 'none' }}
                          >
                            <i className={`bi ${isLiked ? 'bi-heart-fill text-danger' : 'bi-heart'} me-1`}></i>
                            <span className="small">{post.likes_count || 0}</span>
                          </button>

                          <button
                            className="btn btn-link p-0 text-decoration-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCommentModal(post);
                            }}
                            style={{ border: 'none', background: 'none' }}
                          >
                            <i className="bi bi-chat-dots me-1 text-primary"></i>
                            <span className="small">{post.comments?.length || 0}</span>
                          </button>

{/* <button
  className="btn btn-link p-0 text-decoration-none"
  onClick={(e) => {
    e.stopPropagation();
    handleShare(post.id);
  }}
  style={{ border: 'none', background: 'none' }}
>
  <i className="bi bi-share me-1 text-primary"></i>
  <span className="small">Share</span>
</button> */}

                          <button
                            className="btn btn-link p-0 text-decoration-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSave(post.id);
                            }}
                            style={{ border: 'none', background: 'none' }}
                            title={isSaved ? 'Unsave' : 'Save'}
                          >
                            <i className={`bi ${isSaved ? 'bi-bookmark-fill text-dark' : 'bi-bookmark'} me-1`}></i>
                          </button>
                        </div>
                        <button
                          className="btn btn-primary btn-sm rounded-pill px-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/post/${post.id}`);
                          }}
                        >
                          More <i className="bi bi-arrow-right ms-1"></i>
                        </button>
                      </div>

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
        })()}

      </div>

      <PostCommentModal
        post={commentModalPost}
        isOpen={!!commentModalPost}
        onClose={closeCommentModal}
        commentText={commentText}
        onCommentTextChange={setCommentText}
        onSubmitComment={handleComment}
        onLike={() => commentModalPost && handleLike(commentModalPost.id)}
        // onShare={() => commentModalPost && handleShare(commentModalPost.id)}
        onSave={() => commentModalPost && handleSave(commentModalPost.id)}
        isLiked={commentModalPost ? likedPosts.has(commentModalPost.id) : false}
        isSaved={commentModalPost ? savedPosts.has(commentModalPost.id) : false}
      />

      <style>{`
        .hover-shadow {
          transition: all 0.3s ease;
        }
        .hover-shadow:hover {
          transform: translateY(-3px);
          box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,.15) !important;
        }
        .card-body img {
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
}

export default SocialFeed;
